import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, isFeatureEnabled } from '../config/environment';

import { ChevronRight, Instagram, Facebook } from 'lucide-react';


import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';

// Stałe dla kolorów
const COLORS = {
  // Ciepły beż jako główny kolor
  primary: '#D4A373',  // Ciepły beż
  primaryLight: '#E6CCB2', // Jaśniejszy beż
  primaryDark: '#B88A5F',  // Ciemniejszy beż
  
  // Zieleń jako tło i akcenty
  secondary: '#2F4333', // Ciemna zieleń (tło)
  secondaryLight: '#3D5548', // Jaśniejsza zieleń
  
  // Dodatkowe kolory
  cream: '#FAF3E0', // Kremowy
  warmGray: '#7D6E83' // Ciepły szary
};

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);

  const [currentSection, setCurrentSection] = useState('home');

  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const sectionRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const importImages = async () => {
      try {
        const context = require.context('../../public/img/', false, /\.(png|jpe?g|webp)$/);
        const imageUrls = context.keys().map(context);
        setImages(imageUrls);
      } catch (error) {
        console.error('Błąd podczas ładowania zdjęć:', error);
      }
    };

    importImages();
  }, []);

  
  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');
    const navHeight = 80; // wysokość nawigacji
  
    // Funkcja do płynnego przewijania
    const smoothScroll = (e) => {
      e.preventDefault();
      const targetId = e.target.getAttribute('href').substring(1);
      const targetSection = document.getElementById(targetId);
      if (targetSection) {
        const targetPosition = targetSection.offsetTop - navHeight;
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    };
  
    // Obsługa przewijania i aktywnej sekcji
    const handleScroll = () => {
      const pageYOffset = window.pageYOffset + navHeight + 100; // offset dla lepszej detekcji
      let newCurrentSection = currentSection;
  
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        
        if (pageYOffset >= sectionTop && pageYOffset < sectionTop + sectionHeight) {
          newCurrentSection = section.getAttribute('id');
        }
      });
  
      if (newCurrentSection !== currentSection) {
        setCurrentSection(newCurrentSection);
      }
    };
  
    // Nasłuchiwanie na scroll
    window.addEventListener('scroll', handleScroll);
    
    // Dodaj obsługę płynnego przewijania do linków nawigacji
    const navLinks = document.querySelectorAll('a[href^="#"]');
    navLinks.forEach(link => {
      link.addEventListener('click', smoothScroll);
    });
  
    return () => {
      window.removeEventListener('scroll', handleScroll);
      navLinks.forEach(link => {
        link.removeEventListener('click', smoothScroll);
      });
    };
  }, [currentSection]);


  useEffect(() => {
    const sectionObserver = new IntersectionObserver(
      ([entry]) => {
        setIsMenuVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1
      }
    );

    const stickyObserver = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: 1,
        rootMargin: "-50% 0px -50% 0px"
      }
    );

    if (sectionRef.current) {
      sectionObserver.observe(sectionRef.current);
      stickyObserver.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        sectionObserver.unobserve(sectionRef.current);
        stickyObserver.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const updateButtonPosition = () => {
      if (buttonRef.current && sectionRef.current && isSticky) {
        const sectionRect = sectionRef.current.getBoundingClientRect();
        const rightOffset = window.innerWidth - sectionRect.right;
        buttonRef.current.style.right = `${Math.max(rightOffset, 0)}px`;
      }
    };

    window.addEventListener('resize', updateButtonPosition);
    updateButtonPosition();

    return () => window.removeEventListener('resize', updateButtonPosition);
  }, [isSticky]);

  return (
    <div className="min-h-screen font-montserrat bg-[#2F4333]">
      {/* Hero Section */}
      <section id="home" className="relative h-screen flex flex-col md:flex-row bg-[#2F4333] overflow-hidden">
  {/* Video Container - bez zmian */}
  <div className="absolute inset-0 md:relative md:w-[45%] h-full overflow-hidden">
    <video
      autoPlay
      muted
      loop
      playsInline
      className="h-full w-full object-cover absolute inset-0"
    >
      <source src="/1.MOV" type="video/mp4" />
    </video>
    <div className="absolute inset-0 bg-gradient-to-r from-[#2F4333]/20 to-[#2F4333]/80 md:bg-gradient-to-r md:from-transparent md:to-[#2F4333]" />
  </div>

  {/* Content Container */}
  <div className="relative md:w-[55%] h-full flex items-center justify-center z-10 px-4 sm:px-6 md:px-8 lg:px-12">
    {/* Mobile Background */}
    <div className="absolute inset-0 md:hidden bg-[#2F4333]/40 backdrop-blur-[2px]" />
    
    {/* Content Wrapper - zmniejszony max-width dla PC */}
    <div className="relative z-20 w-full max-w-[340px] xs:max-w-[440px] sm:max-w-xl md:max-w-2xl lg:max-w-3xl text-center">
      {/* Welcome Text - zmniejszony na PC */}
      <span className="block text-[#D4A373] font-montserrat uppercase tracking-wider 
        text-lg xs:text-xl sm:text-2xl md:text-2xl lg:text-2xl animate-fade-in-down md:drop-shadow-none">
        {t('hero.welcome')}
      </span>

      {/* Main Title - zmniejszony na PC */}
      <h1 className="font-cormorant-garamond text-5xl xs:text-6xl sm:text-7xl md:text-7xl lg:text-7xl
        font-bold text-[#FAF3E0] mt-4 mb-6 sm:my-8 animate-fade-in-down animation-delay-200
        drop-shadow-[0_4px_4px_rgba(0,0,0,0.3)] md:drop-shadow-none leading-[1.1]">
        {t('hero.title')}
      </h1>

      {/* Subtitle - zmniejszony na PC */}
      <p className="text-lg xs:text-xl sm:text-2xl md:text-2xl lg:text-2xl text-[#E6CCB2] 
        mb-10 sm:mb-12 md:mb-16 font-montserrat font-light animate-fade-in-down animation-delay-400
        drop-shadow-lg md:drop-shadow-none max-w-[95%] mx-auto leading-relaxed">
        {t('hero.subtitle')}
      </p>

      {/* Buttons Container */}
      <div className="flex flex-col xs:flex-row flex-wrap gap-4 sm:gap-6 justify-center 
        animate-fade-in-down animation-delay-600 px-2 xs:px-0">
        <button 
          onClick={() => window.open('https://restauracjatortuga.goorder.pl/', '_blank')}
          className="group relative bg-[#D4A373] hover:bg-[#B88A5F] text-[#2F4333] 
            px-8 sm:px-10 md:px-12 py-4 sm:py-5 rounded-lg 
            text-lg sm:text-xl md:text-xl lg:text-xl
            transition-all duration-300 transform hover:scale-105 shadow-lg
            min-h-[44px] touch-target w-full xs:w-auto font-medium"
        >
          {t('hero.orderDelivery')}
        </button>
        
        <button 
          onClick={() => document.getElementById('events').scrollIntoView({ behavior: 'smooth' })}
          className="group relative border-2 border-[#D4A373] text-[#FAF3E0] 
            px-8 sm:px-10 md:px-12 py-4 sm:py-5 rounded-lg 
            text-lg sm:text-xl md:text-xl lg:text-xl
            transition-all duration-300 transform hover:scale-105 shadow-lg
            min-h-[44px] touch-target w-full xs:w-auto font-medium"
        >
          {t('hero.organizeEvent')}
        </button>
      </div>

      {/* Social Media Icons - zmniejszone na PC */}
      <div className="flex justify-center gap-8 sm:gap-10 mt-12 sm:mt-16 
        animate-fade-in-down animation-delay-800">
        <a 
          href="https://www.instagram.com/restauracja_tortuga/" 
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
          className="text-[#D4A373] hover:text-[#B88A5F] transition-colors duration-300
            min-h-[48px] min-w-[48px] flex items-center justify-center touch-target"
        >
          <Instagram className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10" />
        </a>
        <a 
          href="https://www.facebook.com/restauracjatortuga/" 
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Facebook"
          className="text-[#D4A373] hover:text-[#B88A5F] transition-colors duration-300
            min-h-[48px] min-w-[48px] flex items-center justify-center touch-target"
        >
          <Facebook className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10" />
        </a>
      </div>
    </div>
  </div>
</section>

      {/* Wydarzenia */}
      <section id="events" className="relative min-h-screen h-[100dvh] flex flex-col md:flex-row bg-[#D4A373] overflow-hidden">
  {/* Mobile video with overlay */}
  <div className="absolute inset-0 md:relative md:w-[45%] h-full overflow-hidden md:hidden">
    <div className="relative h-full w-full">
      <video
        autoPlay
        muted
        loop
        playsInline
        className="h-full w-full object-cover absolute inset-0"
        style={{ 
          objectFit: 'cover', 
          objectPosition: 'center',
          transform: 'scale(1.02)' 
        }}
      >
        <source src="/2.MOV" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-t from-[#D4A373]/20 to-transparent" />
    </div>
  </div>

  {/* Content Container */}
  <div className="relative md:w-[55%] h-full flex items-center z-10 px-4 xs:px-6 sm:px-8 md:px-12 lg:px-16">
    {/* Mobile Background Overlay */}
    <div className="absolute inset-0 md:hidden bg-[#D4A373]/10 backdrop-blur-[2px]" />

    {/* Mobile/Small Screen Version */}
    <div className="relative z-20 w-full max-w-[340px] xs:max-w-[440px] sm:max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto 
      text-center space-y-4 sm:space-y-6 md:hidden">
      <div className="bg-[#E6CCB2]/70 p-4 xs:p-6 sm:p-8 rounded-lg transform -translate-y-4 opacity-0 
        animate-fade-in-down animation-delay-200">
        <h2 className="font-cormorant-garamond text-5xl xs:text-6xl sm:text-7xl font-bold text-[#2F4333] 
          mb-3 xs:mb-4 sm:mb-6">
          {t('events.title')}
        </h2>
        <p className="text-lg xs:text-xl sm:text-2xl text-[#2F4333] font-montserrat max-w-xl mx-auto 
          font-medium">
          {t('events.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:gap-6
        transform -translate-y-4 opacity-0 animate-fade-in-down animation-delay-400">
        <div className="bg-[#E6CCB2]/70 p-4 xs:p-5 sm:p-6 rounded-lg">
          <h4 className="font-cormorant-garamond text-3xl xs:text-4xl font-semibold text-[#2F4333] mb-2">
            {t('events.corporate.title')}
          </h4>
          <p className="text-base xs:text-lg sm:text-xl text-[#2F4333]/90 font-medium leading-relaxed">
            {t('events.corporate.description')}
          </p>
        </div>
        <div className="bg-[#E6CCB2]/70 p-4 xs:p-5 sm:p-6 rounded-lg">
          <h4 className="font-cormorant-garamond text-3xl xs:text-4xl font-semibold text-[#2F4333] mb-2">
            {t('events.private.title')}
          </h4>
          <p className="text-base xs:text-lg sm:text-xl text-[#2F4333]/90 font-medium leading-relaxed">
            {t('events.private.description')}
          </p>
        </div>
      </div>
    </div>

    {/* Desktop Version */}
    <div className="relative z-20 max-w-2xl mx-auto text-center space-y-8 hidden md:block">
      <div className="transform -translate-y-4 opacity-0 animate-fade-in-down animation-delay-200">
        <h2 className="font-cormorant-garamond text-6xl md:text-7xl font-bold text-[#2F4333] mb-6">
          {t('events.title')}
        </h2>
        <p className="text-xl md:text-2xl text-[#2F4333] font-montserrat max-w-xl mx-auto font-medium">
          {t('events.description')}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 transform -translate-y-4 opacity-0 
        animate-fade-in-down animation-delay-400">
        <div>
          <h4 className="font-cormorant-garamond text-3xl md:text-4xl font-semibold text-[#2F4333] mb-3">
            {t('events.corporate.title')}
          </h4>
          <p className="text-lg md:text-xl text-[#2F4333]/90 font-medium leading-relaxed">
            {t('events.corporate.description')}
          </p>
        </div>
        <div>
          <h4 className="font-cormorant-garamond text-3xl md:text-4xl font-semibold text-[#2F4333] mb-3">
            {t('events.private.title')}
          </h4>
          <p className="text-lg md:text-xl text-[#2F4333]/90 font-medium leading-relaxed">
            {t('events.private.description')}
          </p>
        </div>
      </div>
    </div>

    {/* Contact Button */}
    <div className="absolute left-0 right-0 bottom-8 xs:bottom-12 z-20">
      <button
        onClick={() => document.getElementById('footer').scrollIntoView({ behavior: 'smooth' })}
        className="mx-auto block group relative 
          bg-[#2F4333] text-[#FAF3E0] 
          px-8 sm:px-10 py-4 sm:py-5 rounded-lg 
          text-lg sm:text-xl md:text-xl
          transition-all duration-300 transform hover:scale-105 shadow-lg
          opacity-0 animate-fade-in-up animation-delay-600
          min-h-[44px] touch-target"
      >
        <span className="relative z-10">
          {t('events.contactUs')}
        </span>
        <div className="absolute inset-0 bg-[#3D5548] transform scale-x-0 
          group-hover:scale-x-100 transition-transform duration-300 origin-left 
          rounded-lg" />
      </button>
    </div>
  </div>

  {/* Desktop Video */}
  <div className="hidden md:block absolute right-0 w-[45%] h-full overflow-hidden">
    <div className="relative h-full w-full">
      <video
        autoPlay
        muted
        loop
        playsInline
        className="h-full w-full object-cover absolute inset-0"
        style={{ 
          objectFit: 'cover', 
          objectPosition: 'center',
          transform: 'scale(1.02)' 
        }}
      >
        <source src="/2.MOV" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-r from-[#D4A373] via-[#D4A373]/20 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#D4A373]/10 to-transparent" />
    </div>
  </div>

  {/* Overlay for smooth transition */}
  <div className="absolute inset-0 hidden md:block pointer-events-none">
    <div className="absolute left-0 w-1/2 h-full bg-gradient-to-r from-[#D4A373] via-[#D4A373] to-transparent" 
      style={{ left: '15%' }} />
  </div>
</section>

{/* Menu Section - Without Global Styles */}
<section ref={sectionRef} id="menu" className="relative py-12 xs:py-16 sm:py-20 md:py-24 bg-[#FAF3E0]">
  <div className="relative max-w-7xl mx-auto pt-20 md:pt-16"> {/* Padding dla navbara */}
    {/* Title Container */}
    <div className="max-w-3xl mx-auto text-center mb-12 xs:mb-16 sm:mb-20 px-4 xs:px-6 sm:px-8">
      {/* Main Title */}
      <h2 className="text-5xl xs:text-6xl sm:text-7xl md:text-7xl font-cormorant-garamond text-[#2F4333] 
        font-bold leading-tight mb-6 xs:mb-8 sm:mb-10">
        {t('menu.title2')}
      </h2>
      
      {/* Description */}
      <p className="text-lg xs:text-xl sm:text-2xl md:text-2xl font-cormorant-garamond text-[#2F4333]/90 
        leading-relaxed font-medium max-w-2xl mx-auto">
        {t('menu.discription')}
      </p>
    </div>
    
    {/* Grid Container */}
    <div className="max-w-6xl mx-auto px-4 xs:px-6 sm:px-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 xs:gap-5 sm:gap-6 md:gap-8">
        {images.slice(0, 6).map((imageUrl, index) => (
          <div 
            key={index}
            className="aspect-[4/3] overflow-hidden rounded-xl shadow-lg 
              transform transition-all duration-500 hover:scale-105
              group relative"
          >
            <img
              src={imageUrl}
              alt={`Menu item ${index + 1}`}
              className="w-full h-full object-cover transform transition-transform duration-500
                group-hover:scale-110"
              onError={(e) => {
                console.error(`Błąd ładowania zdjęcia ${index + 1}:`, e);
              }}
            />
          </div>
        ))}
      </div>
    </div>

    {/* Floating Menu Button */}
    <div 
      ref={buttonRef}
      className={`transition-all duration-300 z-50
        ${isSticky ? 'fixed' : 'absolute'} 
        ${isMenuVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}
      `}
      style={{
        top: '50%',
        transform: 'translateY(-50%)'
      }}
    >
      <button
        onClick={() => window.open('https://restauracjatortuga.goorder.pl/', '_blank')}
        aria-label="Zobacz pełne menu"
        className="group relative bg-[#D4A373]/90 hover:bg-[#D4A373] text-[#2F4333] 
          rounded-l-lg shadow-xl transition-all duration-300
          flex items-center space-x-2 px-6 sm:px-8 py-4 sm:py-5
          min-h-[44px] touch-target"
      >
        <span className="font-cormorant-garamond text-lg sm:text-xl md:text-xl font-semibold 
          whitespace-nowrap">
          {t('menu.fullMenu')}
        </span>
        <ChevronRight 
          className="w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-300 
          group-hover:translate-x-1" 
        />
      </button>
    </div>
  </div>
</section>

      {/* Footer */}
      <section id="footer">
  <footer className="bg-[#2F4333] text-[#FAF3E0]">
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Kontakt */}
        <div>
          <h3 className="text-2xl text-center font-cormorant-garamond font-semibold mb-4">
            {t('footer.contact')}
          </h3>
          <ul className="space-y-2 text-center font-montserrat">
            <li>
              <a href="tel:+48577590" className="hover:text-[#D4A373] transition">
                {t('footer.address.phone')}
              </a>
            </li>
            <li>
              <a href="mailto:restauracjatortuga@gmail.com" className="hover:text-[#D4A373] transition">
                {t('footer.address.email')}
              </a>
            </li>
            <li>{t('footer.address.street')}</li>
            <li>{t('footer.address.city')}</li>
          </ul>
        </div>

        {/* Logo i Social Media */}
        <div className="flex flex-col items-center space-y-6">
          <img 
            src="/logo/logo.png" 
            alt="Restaurant Logo" 
            className="h-[150px] w-auto opacity-90 hover:opacity-100 transition-opacity"
          />
          {/* Social Media Icons */}
          <div className="flex justify-center gap-6">
            <a 
              href="https://www.instagram.com/restauracja_tortuga/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#D4A373] hover:text-[#B88A5F] transition-colors duration-300"
              aria-label="Instagram"
            >
              <Instagram size={24} />
            </a>
            <a 
              href="https://www.facebook.com/restauracjatortuga/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#D4A373] hover:text-[#B88A5F] transition-colors duration-300"
              aria-label="Facebook"
            >
              <Facebook size={24} />
            </a>
          </div>
        </div>

        {/* Mapa */}
        <div>
          <h3 className="text-2xl font-cormorant-garamond font-semibold mb-4">
            {t('footer.findUs')}
          </h3>
          <div className="h-48 rounded-lg overflow-hidden shadow-xl">
            <iframe
              title="Restaurant location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d9398.501206128652!2d14.256308!3d53.920634!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47aa5fedf429e9cb%3A0x84abf1b725210322!2sTortuga%20Food%20%26%20Cocktails!5e0!3m2!1sen!2spl!4v1732634561587!5m2!1sen!2spl"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="mt-12 pt-8 border-t border-[#D4A373]/20 text-center font-montserrat">
        <p>© {new Date().getFullYear()} {t('footer.copyright')}</p>
      </div>
    </div>
  </footer>
</section>

    </div>
  );
};

export default HomePage;